import React, { useMemo, useRef, useState } from "react";
import { config as baseConfig, barConfig } from "../config";
import {
  resolveTokenColor,
  getExtents,
  getStackedExtents,
  computeNiceTicks,
  formatDataValue,
  formatXLabel,
} from "../SupportFiles/chartUtils";
import {
  ClipPathRect,
  BackgroundRect,
  GridVertical,
  GridHorizontal,
  AxisLines,
} from "../SupportFiles/chartSections";
import { useUniqueComponentId } from "../SupportFiles/uniqueId";
import NoData from "../../Info/NoData";
function pathForCentersHorizontal(
  data,
  n,
  centerYFn,
  w,
  p,
  clampToMin,
  curveType,
) {
  if (!data || data.length === 0) return "";
  const xFromVal = (v) => {
    const vv = clampToMin ? Math.max(p.min, v) : v;
    const k = (vv - p.min) / (p.max - p.min);
    return (
      p.left +
      Math.max(0, Math.min(1, isFinite(k) ? k : 0)) * (w - p.left - p.right)
    );
  };
  if (curveType === "smooth" && data.length >= 2) {
    const pts = [];
    for (let i = 0; i < data.length; i++) {
      const x = xFromVal(data[i]);
      const y = centerYFn(i);
      pts.push({ x, y });
    }
    let d = `M${pts[0].x},${pts[0].y}`;
    if (pts.length === 2) {
      d += ` L${pts[1].x},${pts[1].y}`;
      return d;
    }
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i > 0 ? i - 1 : i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
  }
  let d = "";
  for (let i = 0; i < data.length; i++) {
    const x = xFromVal(data[i]);
    const y = centerYFn(i);
    if (i === 0) d = `M${x},${y}`;
    else if (curveType === "stepline") {
      const prevX = xFromVal(data[i - 1]);
      d += ` L${prevX},${y} L${x},${y}`;
    } else {
      d += ` L${x},${y}`;
    }
  }
  return d;
}
function areaPathForCentersHorizontal(
  data,
  n,
  centerYFn,
  w,
  p,
  clampToMin,
  curveType,
) {
  if (!data || data.length === 0) return "";
  const top = pathForCentersHorizontal(
    data,
    n,
    centerYFn,
    w,
    p,
    clampToMin,
    curveType,
  );
  const firstY = centerYFn(0);
  const lastY = centerYFn(n - 1);
  const baseX = p.left;
  return `${top} L${baseX},${lastY} L${baseX},${firstY} Z`;
}
function xScale(v, min, max, w, l, r, clampToMin) {
  const cw = Math.max(0, w - l - r);
  const vv = clampToMin ? Math.max(min, v) : v;
  const k = (vv - min) / (max - min);
  return l + (isFinite(k) ? k : 0) * cw;
}

const BarChartNew = ({
  series = [{ name: "Series 1", data: [] }],
  categories = [],
  title,
  height = 360,
  width = 600,
  colors,
  dataLabelsEnabled = baseConfig?.showLabels?.show || false,
  barOptions,
  edgePadding,
  padding: paddingProp,
  zeroValues,
  axisYWidth: axisYWidthProp,
  colorMap,
  stacked = barConfig?.stacked || false,
  componentId,
  onHover,
}) => {
  const generatedId = useUniqueComponentId("charts-adv");
  const localId = componentId || generatedId;
  const basePad = (paddingProp ?? baseConfig.padding) || {
    top: 16,
    right: 12,
    bottom: 28,
    left: 40,
  };
  const svgW = typeof width === "number" ? width : Number(width) || 600;
  const svgH = typeof height === "number" ? height : Number(height) || 360;
  const pad = {
    top: basePad.top,
    right: basePad.right,
    bottom: basePad.bottom,
    left: basePad.left,
  };
  const axisLabelFontSizePx =
    typeof baseConfig.axis?.fontSize === "number"
      ? baseConfig.axis.fontSize
      : 12;
  const axisLabelColor = resolveTokenColor(baseConfig.axis.labelColor);
  const axisBorderColor = resolveTokenColor(baseConfig.axis.color);
  const gridColor = resolveTokenColor(baseConfig.grid.color);
  const gridBg = resolveTokenColor(baseConfig.grid?.background);
  const ePad =
    typeof edgePadding === "number"
      ? Math.max(0, edgePadding)
      : typeof baseConfig.axis?.edgePadding === "number"
        ? baseConfig.axis.edgePadding
        : 0;
  const axisYWidth =
    typeof axisYWidthProp === "number"
      ? Math.max(0, axisYWidthProp)
      : typeof baseConfig.axis?.y?.width === "number"
      ? Math.max(0, baseConfig.axis.y.width)
      : pad.left;
  const axisXHeight =
    typeof baseConfig.axis?.x?.height === "number"
      ? Math.max(0, baseConfig.axis.x.height)
      : Math.max(24, axisLabelFontSizePx + 12);
  const plotH = Math.max(0, svgH - axisXHeight);
  const containerW = Math.max(1, svgW - axisYWidth);
  const dash = baseConfig.grid?.dasharray || "3 3";
  const axisFontSize =
    typeof baseConfig.axis?.fontSize === "number"
      ? `${baseConfig.axis.fontSize}px`
      : undefined;
  const dataLabelFontSize =
    typeof baseConfig.showLabels?.fontSize === "number"
      ? `${baseConfig.showLabels.fontSize}px`
      : undefined;
  const zeroMode =
    typeof zeroValues?.mode === "string" ? zeroValues.mode : "none";
  const compactZeros = zeroMode === "hide";
  const ghostSizePx =
    typeof zeroValues?.ghostSizePx === "number" && isFinite(zeroValues.ghostSizePx)
      ? Math.max(1, Math.round(zeroValues.ghostSizePx))
      : 3;
  const ghostOpacity =
    typeof zeroValues?.ghostOpacity === "number" && isFinite(zeroValues.ghostOpacity)
      ? Math.max(0, Math.min(1, zeroValues.ghostOpacity))
      : 0.25;
  const ghostFill = resolveTokenColor(
    typeof zeroValues?.ghostColor === "string" ? zeroValues.ghostColor : "gray-400",
  );
  const scrollRef = useRef(null);
  const xAxisContentRef = useRef(null);
  const yAxisContentRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const heightCfg =
    typeof barOptions?.height === "number"
      ? Math.max(1, barOptions.height)
      : typeof barConfig?.height === "number"
        ? Math.max(1, barConfig.height)
        : 30;
  const gapCfg =
    typeof barOptions?.gap === "number"
      ? Math.max(0, barOptions.gap)
      : typeof barConfig?.gap === "number"
        ? Math.max(0, barConfig.gap)
        : 0;
  const groupGapCfg =
    typeof barOptions?.groupGap === "number"
      ? Math.max(0, barOptions.groupGap)
      : typeof barConfig?.groupGap === "number"
        ? Math.max(0, barConfig.groupGap)
        : gapCfg;
  const resolvedColors = useMemo(
    () =>
      (colors || baseConfig.colors).map((c, i) => {
        const resolved =
          (colorMap && colorMap[i]) || resolveTokenColor(c) || "#3b82f6";
        return resolved;
      }),
    [colors, colorMap],
  );
  const visibleSeries = useMemo(
    () => series.filter((s) => !s.__projection),
    [series],
  );
  const innerNameToColorIndex = useMemo(() => {
    const map = new Map();
    for (const s of visibleSeries) {
      const key = s?.name;
      if (!key) continue;
      if (!map.has(key)) map.set(key, map.size);
    }
    return map;
  }, [visibleSeries]);
  const groupIndices = useMemo(() => {
    const set = new Set();
    for (const s of visibleSeries) {
      if (typeof s?.__group === "number" && isFinite(s.__group)) {
        set.add(Number(s.__group));
      }
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [visibleSeries]);
  const groupedStacked = stacked && groupIndices.length > 0;
  const groupedLayout = useMemo(() => {
    if (!groupedStacked) {
      return { sumByCategory: null, activeGroupsByCategory: null, maxActive: 0 };
    }
    const nCatsLocal = Math.max(0, categories.length);
    const sumByCategory = Array.from({ length: nCatsLocal }, () => new Map());
    for (const s of visibleSeries) {
      const gRaw = s?.__group;
      const g = gRaw != null && isFinite(gRaw) ? Number(gRaw) : null;
      if (g == null) continue;
      const arr = Array.isArray(s?.data) ? s.data : [];
      for (let ci = 0; ci < nCatsLocal; ci += 1) {
        const vRaw = arr[ci];
        const v = vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
        if (v === 0) continue;
        const m = sumByCategory[ci];
        m.set(g, (m.get(g) || 0) + v);
      }
    }
    const activeGroupsByCategory = [];
    let maxActive = 0;
    for (let ci = 0; ci < nCatsLocal; ci += 1) {
      const m = sumByCategory[ci];
      const act = compactZeros
        ? groupIndices.filter((g) => (m.get(g) || 0) !== 0)
        : groupIndices.slice();
      activeGroupsByCategory.push(act);
      if (act.length > maxActive) maxActive = act.length;
    }
    return { sumByCategory, activeGroupsByCategory, maxActive };
  }, [groupedStacked, categories.length, visibleSeries, groupIndices, compactZeros]);
  const { min, max } = useMemo(() => {
    if (groupedStacked) {
      let maxV = 0;
      const nCatsLocal = Math.max(0, categories.length);
      const sumByCategory = groupedLayout?.sumByCategory;
      if (sumByCategory) {
        for (let ci = 0; ci < nCatsLocal; ci += 1) {
          const m = sumByCategory[ci];
          for (const g of groupIndices) {
            const sum = m.get(g) || 0;
            if (sum > maxV) maxV = sum;
          }
        }
        return { min: 0, max: maxV };
      }
      for (let ci = 0; ci < nCatsLocal; ci += 1) {
        for (const g of groupIndices) {
          let sum = 0;
          for (const s of visibleSeries) {
            if (Number(s?.__group) !== g) continue;
            const vRaw = s?.data?.[ci];
            sum += vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
          }
          if (sum > maxV) maxV = sum;
        }
      }
      return { min: 0, max: maxV };
    }
    if (stacked) {
      return getStackedExtents(visibleSeries, categories.length, true);
    }
    return getExtents(visibleSeries, true);
  }, [
    groupedStacked,
    groupIndices,
    visibleSeries,
    categories.length,
    stacked,
    groupedLayout?.sumByCategory,
  ]);
  const nCatsRaw = Math.max(0, categories.length);
  const barH = Math.max(1, heightCfg);
  const bandGap = Math.max(0, gapCfg);
  const labelOnlyHeightPx = Math.max(18, Math.round(axisLabelFontSizePx + 10));
  const bandMeta = useMemo(() => {
    const yMin = pad.top + ePad;
    let y = yMin;
    const yStarts = [];
    const bandHeights = [];
    const labelCenters = [];
    const barsStarts = [];
    for (let ci = 0; ci < nCatsRaw; ci += 1) {
      let activeCount = 0;
      if (groupedStacked) {
        const activeGroups = compactZeros
          ? groupedLayout.activeGroupsByCategory?.[ci] || []
          : groupIndices;
        activeCount = activeGroups.length;
      } else if (stacked) {
        const sum = visibleSeries.reduce((acc, s) => {
          const vRaw = s?.data?.[ci];
          return acc + (vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0);
        }, 0);
        activeCount = compactZeros && sum === 0 ? 0 : 1;
      } else {
        const count = visibleSeries.reduce((acc, s) => {
          const vRaw = s?.data?.[ci];
          const v = vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
          return acc + (compactZeros ? (v !== 0 ? 1 : 0) : 1);
        }, 0);
        activeCount = count;
      }
      const usedBarsH =
        activeCount > 0
          ? activeCount * barH + Math.max(0, (activeCount - 1) * bandGap)
          : 0;
      const mainH = Math.max(labelOnlyHeightPx, usedBarsH || labelOnlyHeightPx);
      const gapAfter = activeCount > 0 ? groupGapCfg : 0;
      const bandH = mainH + gapAfter;
      yStarts.push(y);
      bandHeights.push(bandH);
      labelCenters.push(y + mainH / 2);
      barsStarts.push(
        y + Math.max(0, Math.floor((mainH - usedBarsH) / 2)),
      );
      y += bandH;
    }
    return {
      yMin,
      yStarts,
      bandHeights,
      labelCenters,
      barsStarts,
      plotEndY: y,
      contentH: pad.top + pad.bottom + 2 * ePad + Math.max(0, y - yMin),
    };
  }, [
    pad.top,
    pad.bottom,
    ePad,
    nCatsRaw,
    groupedStacked,
    compactZeros,
    groupedLayout.activeGroupsByCategory,
    groupIndices,
    stacked,
    visibleSeries,
    barH,
    bandGap,
    labelOnlyHeightPx,
    groupGapCfg,
  ]);
  const containerH = Math.max(0, plotH);
  const contentH = bandMeta.contentH;
  const scrollEnabled =
    baseConfig.axis?.scroll === "auto"
      ? contentH > containerH
      : !!baseConfig.axis?.scroll;
  const hEff = scrollEnabled ? contentH : containerH;
  const xMinPlot = pad.left;
  const xMaxPlot = containerW - pad.right;
  const cwPlot = Math.max(0, xMaxPlot - xMinPlot);
  const yMinPlot = bandMeta.yMin;
  const yMaxPlot = Math.min(hEff - pad.bottom - ePad, bandMeta.plotEndY);
  const centerY = (i) => bandMeta.labelCenters[i] ?? yMinPlot;
  const findCategoryIndex = (yy) => {
    const starts = bandMeta.yStarts;
    const heights = bandMeta.bandHeights;
    let lo = 0;
    let hi = starts.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const s = starts[mid];
      const e = s + heights[mid];
      if (yy < s) hi = mid - 1;
      else if (yy >= e) lo = mid + 1;
      else return mid;
    }
    return -1;
  };
  const handleMove = (e) => {
    if (draggingRef.current) return;
    const r = scrollRef.current?.getBoundingClientRect();
    if (!r || categories.length < 1) return;
    const xMin = pad.left + ePad;
    const xMax = svgW - pad.right - ePad;
    const yMin = yMinPlot;
    const yMax = yMaxPlot;
    const scrollTop = scrollRef.current?.scrollTop || 0;
    const x = e.clientX - r.left;
    const y = e.clientY - r.top + scrollTop;
    if (x < xMin || x > xMax || y < yMin || y > yMax) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    const idx = findCategoryIndex(y);
    if (idx < 0) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    const cy = centerY(idx);
    let yp = cy;
    let groupSlot = 0;
    if (groupedStacked) {
      const activeGroups = compactZeros
        ? groupedLayout.activeGroupsByCategory?.[idx] || []
        : groupIndices;
      if (compactZeros && activeGroups.length === 0) {
        setHover(null);
        if (onHover) onHover(null);
        return;
      }
      const slotH = barH + bandGap;
      const rel = y - (bandMeta.barsStarts[idx] || yMinPlot);
      groupSlot = slotH > 0 ? Math.floor(rel / slotH) : 0;
      groupSlot = Math.max(0, Math.min(activeGroups.length - 1, groupSlot));
      yp = (bandMeta.barsStarts[idx] || yMinPlot) + groupSlot * slotH + barH / 2;
    }
    setHover({ i: idx, y: yp });
    if (onHover) {
      const activeGroups = compactZeros
        ? groupedLayout.activeGroupsByCategory?.[idx] || []
        : groupIndices;
      const groupIdx = groupedStacked ? activeGroups[groupSlot] : null;
      const hoverSeries = groupedStacked
        ? visibleSeries.filter((s) => Number(s?.__group) === groupIdx)
        : visibleSeries;
      const values = hoverSeries.map((s) => s.data[idx]);
      const rows = groupedStacked
        ? hoverSeries.map((s, si) => {
            const innerColorIndex =
              typeof s?.__colorIdx === "number" && isFinite(s.__colorIdx)
                ? s.__colorIdx
                : typeof s?.name === "string"
                  ? (innerNameToColorIndex.get(s.name) ?? 0)
                  : 0;
            const color =
              resolvedColors[innerColorIndex % resolvedColors.length] ||
              "#3b82f6";
            return {
              name: s?.name || `Series ${si + 1}`,
              color,
              value: formatDataValue(s?.data?.[idx]),
            };
          })
        : undefined;
      const groupName =
        groupedStacked &&
        hoverSeries[0] &&
        typeof hoverSeries[0].__groupName === "string"
          ? hoverSeries[0].__groupName
          : "";
      onHover({
        index: idx,
        guidelineX: r.left + pad.left + 12,
        topY: r.top + yp - scrollTop,
        category: groupName
          ? `${categories[idx]} • ${groupName}`
          : categories[idx],
        values,
        rows,
        componentId: localId,
      });
    }
  };
  const handleLeave = () => {
    setHover(null);
    if (onHover) onHover(null);
  };
  const onPointerDown = (e) => {
    const t = scrollRef.current;
    if (!t) return;
    draggingRef.current = true;
    setDragging(true);
    dragStartYRef.current = e.clientY;
    dragStartScrollRef.current = t.scrollTop || 0;
    if (e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        return;
      }
    }
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const t = scrollRef.current;
    if (!t) return;
    const dy = e.clientY - dragStartYRef.current;
    const max = Math.max(0, t.scrollHeight - t.clientHeight);
    const next = Math.min(Math.max(0, dragStartScrollRef.current - dy), max);
    t.scrollTop = next;
  };
  const onPointerUp = () => {
    draggingRef.current = false;
    setDragging(false);
  };
  const onTouchStart = (e) => {
    const t = scrollRef.current;
    if (!t) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    draggingRef.current = true;
    setDragging(true);
    dragStartYRef.current = touch.clientY;
    dragStartScrollRef.current = t.scrollTop || 0;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const t = scrollRef.current;
    if (!t) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    const dy = touch.clientY - dragStartYRef.current;
    const max = Math.max(0, t.scrollHeight - t.clientHeight);
    const next = Math.min(Math.max(0, dragStartScrollRef.current - dy), max);
    t.scrollTop = next;
  };
  const onTouchEnd = () => {
    draggingRef.current = false;
    setDragging(false);
  };
  const onScroll = () => {
    const el = scrollRef.current;
    const xel = xAxisContentRef.current;
    const yel = yAxisContentRef.current;
    if (!el || !xel) return;
    const sl = el.scrollLeft || 0;
    if (xel.style.transform !== `translateX(${-sl}px)`) {
      xel.style.transform = `translateX(${-sl}px)`;
    }
    const st = el.scrollTop || 0;
    if (yel && yel.style.transform !== `translateY(${-st}px)`) {
      yel.style.transform = `translateY(${-st}px)`;
    }
  };
  const ticksSetup = (() => {
    const tickTargetPx =
      typeof baseConfig.axis?.x?.tickTargetPx === "number"
        ? baseConfig.axis.x.tickTargetPx
        : 80;
    const interior =
      typeof baseConfig.axis?.x?.gridLines === "number"
        ? baseConfig.axis.x.gridLines
        : Math.max(2, Math.round(cwPlot / tickTargetPx));
    return computeNiceTicks(min, max, interior, true);
  })();
  const { ticks: ticksX, step: tickStep, niceMin, niceMax } = ticksSetup;
  const p = { ...pad, min: niceMin, max: niceMax };
  const cornerRadius =
    typeof barOptions?.radius === "number"
      ? Math.max(0, barOptions.radius)
      : typeof barConfig?.radius === "number"
        ? Math.max(0, barConfig.radius)
        : 0;
  const isEmpty = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return true;
    if (!Array.isArray(visibleSeries) || visibleSeries.length === 0)
      return true;
    let hasAny = false;
    let hasNonZero = false;
    for (const s of visibleSeries) {
      const arr = Array.isArray(s?.data) ? s.data : [];
      if (arr.length > 0) hasAny = true;
      for (const v of arr) {
        const n = v != null && isFinite(v) ? Number(v) : 0;
        if (n !== 0) {
          hasNonZero = true;
          break;
        }
      }
      if (hasNonZero) break;
    }
    if (!hasAny) return true;
    if (!hasNonZero) return zeroMode !== "ghost";
    return false;
  }, [categories, visibleSeries, zeroMode]);
  if (isEmpty) {
    const noDataCfg = baseConfig?.noDataConfig || {};
    const showNoData =
      typeof noDataCfg.show === "boolean" ? noDataCfg.show : true;
    return (
      <div
        className="chartWrapper w-full h-full flex"
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
        }}
      >
        {showNoData ? (
          <NoData
            title={noDataCfg.title}
            desc={noDataCfg.desc}
            color={noDataCfg.color}
            noImage={noDataCfg.noImage}
          />
        ) : (
          <div className="w-full h-full relative p-6">
            <svg
              className="w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
              {[20, 35, 50, 65, 80].map((y) => (
                <line
                  key={`g-${y}`}
                  x1="10"
                  y1={y}
                  x2="98"
                  y2={y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              ))}
              <line x1="10" y1="10" x2="10" y2="90" stroke="#e5e7eb" />
              <line x1="10" y1="90" x2="98" y2="90" stroke="#e5e7eb" />
              {[
                { y: 18, w: 78 },
                { y: 32, w: 64 },
                { y: 46, w: 86 },
                { y: 60, w: 50 },
                { y: 74, w: 70 },
              ].map((r, i) => (
                <g key={`r-${i}`}>
                  <rect
                    x="12"
                    y={r.y - 3}
                    width="14"
                    height="6"
                    fill="#f3f4f6"
                    rx="2"
                  />
                  <rect
                    x="28"
                    y={r.y - 4}
                    width={r.w * 0.75}
                    height="8"
                    fill="#f3f4f6"
                    rx="2"
                  />
                  <rect
                    x="28"
                    y={r.y - 4}
                    width={r.w}
                    height="8"
                    fill="#e5e7eb"
                    rx="2"
                  />
                </g>
              ))}
            </svg>
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-gray-500">
              No data available
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      <div className="chartWrapper w-full h-full grid grid-cols-[auto_1fr]">
        <div
          className="yAxis h-full"
          style={{ width: axisYWidth, height: plotH, overflow: "hidden" }}
        >
          <div
            ref={yAxisContentRef}
            style={{ width: axisYWidth, height: hEff, willChange: "transform" }}
          >
            <svg width={axisYWidth} height={hEff}>
              <g>
                {categories.map((c, i) => {
                  const y = centerY(i);
                  return (
                    <text
                      key={`yl-${i}`}
                      x={axisYWidth - 6}
                      y={y + 4}
                      fill={axisLabelColor}
                      fontSize={axisFontSize}
                      textAnchor="end"
                    >
                      {c}
                    </text>
                  );
                })}
              </g>
            </svg>
          </div>
        </div>
        <div className="chartContainer flex flex-col">
          <div
            className="chartArea"
            ref={scrollRef}
            style={{
              overflowY: scrollEnabled ? "auto" : "hidden",
              overflowX: "hidden",
              height: plotH,
              width: containerW,
              cursor: scrollEnabled
                ? dragging
                  ? "grabbing"
                  : "grab"
                : "default",
              userSelect: dragging ? "none" : "auto",
              touchAction: "none",
            }}
            onScroll={onScroll}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <svg
              width={containerW}
              height={hEff}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
            >
              <ClipPathRect
                id={`plotClip-${localId}`}
                x={p.left}
                y={p.top}
                width={containerW - p.left - p.right}
                height={hEff - p.top - p.bottom}
              />
              <BackgroundRect
                x={p.left}
                y={p.top}
                width={containerW - p.left - p.right}
                height={hEff - p.top - p.bottom}
                fill={gridBg}
              />
              <g clipPath={`url(#plotClip-${localId})`}>
                {baseConfig.grid.horizontal && (
                  <GridHorizontal
                    ys={categories.map((_, i) => centerY(i))}
                    left={p.left}
                    right={containerW - p.right}
                    color={gridColor}
                    dash={dash}
                    strokeWidth={baseConfig.grid.strokeWidth || 1}
                  />
                )}
                {baseConfig.grid.vertical && (
                  <GridVertical
                    xs={ticksX
                      .filter((_, idx) => idx > 0 && idx < ticksX.length - 1)
                      .map((tv) =>
                        xScale(
                          tv,
                          p.min,
                          p.max,
                          containerW,
                          p.left,
                          p.right,
                          false,
                        ),
                      )}
                    top={p.top}
                    bottom={hEff - p.bottom}
                    color={gridColor}
                    dash={dash}
                    strokeWidth={baseConfig.grid.strokeWidth || 1}
                  />
                )}
              </g>
              <g>
                <AxisLines
                  xAxis={{
                    x1: p.left,
                    x2: containerW - p.right,
                    y: hEff - p.bottom,
                  }}
                  yAxis={{ x: p.left, y1: p.top, y2: hEff - p.bottom }}
                  color={axisBorderColor}
                />
              </g>
              <g clipPath={`url(#plotClip-${localId})`}>
                {series.map((s, si) => {
                  if (!s.__projection) return null;
                  const color =
                    (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                    resolvedColors[
                      ((s && s.__idx) ?? si) % resolvedColors.length
                    ] ||
                    "#6b7280";
                  const curveType = baseConfig.projection?.curve || "straight";
                  const showArea = !!baseConfig?.projection?.area?.show;
                  const dArea = showArea
                    ? areaPathForCentersHorizontal(
                        s.data,
                        s.data.length,
                        centerY,
                        containerW,
                        p,
                        true,
                        curveType,
                      )
                    : "";
                  const dProj = pathForCentersHorizontal(
                    s.data,
                    s.data.length,
                    centerY,
                    containerW,
                    p,
                    true,
                    curveType,
                  );
                  const dashProj =
                    baseConfig.projection?.dasharray ||
                    baseConfig.grid?.dasharray ||
                    "3 3";
                  return (
                    <g key={`proj-${si}`}>
                      {dArea && (
                        <path
                          d={dArea}
                          fill={resolveTokenColor(color)}
                          opacity={
                            typeof baseConfig?.projection?.area?.fill
                              ?.opacity === "number"
                              ? baseConfig.projection.area.fill.opacity
                              : 0.1
                          }
                          clipPath={`url(#plotClip-${localId})`}
                        />
                      )}
                      <path
                        d={dProj}
                        fill="none"
                        stroke={resolveTokenColor(color)}
                        strokeWidth={
                          typeof baseConfig.projection?.strokeWidth === "number"
                            ? baseConfig.projection.strokeWidth
                            : 1
                        }
                        strokeDasharray={dashProj}
                        strokeLinecap={
                          baseConfig.projection?.lineCap || "round"
                        }
                      />
                    </g>
                  );
                })}
              </g>
              <g clipPath={`url(#plotClip-${localId})`}>
                {categories.map((cat, ci) => {
                  const cy = centerY(ci);
                  const activeSeries =
                    !stacked && compactZeros
                      ? visibleSeries.filter((s) => {
                          const vRaw = s?.data?.[ci];
                          const v = vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
                          return v !== 0;
                        })
                      : visibleSeries;
                  const yBase = bandMeta.barsStarts[ci] ?? cy;
                  return (
                    <g key={`g-${ci}`}>
                      {!stacked &&
                        activeSeries.map((s, si) => {
                          const color =
                            (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                            resolvedColors[
                              ((s && s.__idx) ?? si) % resolvedColors.length
                            ] ||
                            "#3b82f6";
                          const vRaw = s.data[ci];
                          if (vRaw == null || !isFinite(vRaw)) return null;
                          const v = Number(vRaw);
                          if (v === 0 && zeroMode === "hide") return null;
                          const x = xScale(
                            v,
                            p.min,
                            p.max,
                            containerW,
                            p.left,
                            p.right,
                            true,
                          );
                          const baseX = xScale(
                            0,
                            p.min,
                            p.max,
                            containerW,
                            p.left,
                            p.right,
                            true,
                          );
                          const y = yBase + si * (barH + bandGap);
                          const w =
                            v === 0 && zeroMode === "ghost"
                              ? ghostSizePx
                              : Math.max(0, x - baseX);
                          return (
                            <React.Fragment key={`frag-${ci}-${si}`}>
                              <rect
                                x={baseX}
                                y={y}
                                width={w}
                                height={barH}
                                rx={cornerRadius}
                                ry={cornerRadius}
                                style={{
                                  fill:
                                    v === 0 && zeroMode === "ghost"
                                      ? ghostFill
                                      : color,
                                  opacity:
                                    v === 0 && zeroMode === "ghost"
                                      ? ghostOpacity
                                      : 1,
                                }}
                              />
                              {dataLabelsEnabled && w > 12 && (
                                <text
                                  x={baseX + w + 6}
                                  y={y + barH / 2 + 4}
                                  style={{ fill: color }}
                                  fontSize={dataLabelFontSize}
                                  textAnchor="start"
                                >
                                  {formatDataValue(v)}
                                </text>
                              )}
                            </React.Fragment>
                          );
                        })}
                      {stacked &&
                        groupedStacked &&
                        (() => {
                          const baseX = xScale(
                            0,
                            p.min,
                            p.max,
                            containerW,
                            p.left,
                            p.right,
                            true,
                          );
                          const activeGroups = compactZeros
                            ? groupedLayout.activeGroupsByCategory?.[ci] || []
                            : groupIndices;
                          if (compactZeros && activeGroups.length === 0) return null;
                          const yBaseGroups = bandMeta.barsStarts[ci] ?? cy;
                          return (
                            <React.Fragment>
                              {activeGroups.map((g, gi) => {
                                let cum = 0;
                                const stackHeight = barH;
                                const y = Math.round(
                                  yBaseGroups + gi * (barH + bandGap),
                                );
                                const clipId = `clip-${localId}-h-${ci}-${g}`;
                                const groupSeries = visibleSeries.filter(
                                  (s) => Number(s?.__group) === g,
                                );
                                const sumCat =
                                  groupedLayout?.sumByCategory?.[ci]?.get(g) ||
                                  groupSeries.reduce((acc, s) => {
                                    const vRaw = s?.data?.[ci];
                                    return (
                                      acc +
                                      (vRaw != null && isFinite(vRaw)
                                        ? Number(vRaw)
                                        : 0)
                                    );
                                  }, 0);
                                if (sumCat === 0 && zeroMode === "hide") {
                                  return null;
                                }
                                const xTop = xScale(
                                  sumCat,
                                  p.min,
                                  p.max,
                                  containerW,
                                  p.left,
                                  p.right,
                                  true,
                                );
                                const totalW = Math.max(0, xTop - baseX);
                                return (
                                  <React.Fragment key={`gs-${ci}-${g}`}>
                                    {sumCat === 0 && zeroMode === "ghost" ? (
                                      <rect
                                        x={baseX}
                                        y={y}
                                        width={ghostSizePx}
                                        height={stackHeight}
                                        rx={cornerRadius}
                                        ry={cornerRadius}
                                        style={{
                                          fill: ghostFill,
                                          opacity: ghostOpacity,
                                        }}
                                      />
                                    ) : (
                                      <>
                                    <defs>
                                      <clipPath id={clipId}>
                                        <rect
                                          x={baseX}
                                          y={y}
                                          width={totalW}
                                          height={stackHeight}
                                          rx={cornerRadius}
                                          ry={cornerRadius}
                                        />
                                      </clipPath>
                                    </defs>
                                    {groupSeries.map((s, si) => {
                                      const innerColorIndex =
                                        typeof s?.__colorIdx === "number" &&
                                        isFinite(s.__colorIdx)
                                          ? s.__colorIdx
                                          : typeof s?.name === "string"
                                            ? (innerNameToColorIndex.get(
                                                s.name,
                                              ) ?? 0)
                                            : 0;
                                      const color =
                                        resolvedColors[
                                          innerColorIndex %
                                            resolvedColors.length
                                        ] || "#3b82f6";
                                      const vRaw = s?.data?.[ci];
                                      if (vRaw == null || !isFinite(vRaw))
                                        return null;
                                      const v = Number(vRaw);
                                      const xRight = xScale(
                                        cum + v,
                                        p.min,
                                        p.max,
                                        containerW,
                                        p.left,
                                        p.right,
                                        true,
                                      );
                                      const xLeft = xScale(
                                        cum,
                                        p.min,
                                        p.max,
                                        containerW,
                                        p.left,
                                        p.right,
                                        true,
                                      );
                                      const w = Math.max(0, xRight - xLeft);
                                      cum += v;
                                      return (
                                        <rect
                                          key={`gstack-${ci}-${g}-${si}`}
                                          x={xLeft}
                                          y={y}
                                          width={w}
                                          height={stackHeight}
                                          rx={0}
                                          ry={0}
                                          style={{ fill: color }}
                                          clipPath={`url(#${clipId})`}
                                        />
                                      );
                                    })}
                                      </>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })()}
                      {stacked &&
                        !groupedStacked &&
                        (() => {
                          let cum = 0;
                          const baseX = xScale(
                            0,
                            p.min,
                            p.max,
                            containerW,
                            p.left,
                            p.right,
                            true,
                          );
                          const stackHeight = barH;
                          const y = Math.round(bandMeta.barsStarts[ci] ?? cy);
                          const clipId = `clip-${localId}-h-${ci}`;
                          const sumCat = visibleSeries.reduce((acc, s) => {
                            const vRaw = s.data[ci];
                            return (
                              acc +
                              (vRaw != null && isFinite(vRaw)
                                ? Number(vRaw)
                                : 0)
                            );
                          }, 0);
                          if (sumCat === 0 && zeroMode === "hide") return null;
                          const xTop = xScale(
                            sumCat,
                            p.min,
                            p.max,
                            containerW,
                            p.left,
                            p.right,
                            true,
                          );
                          const totalW = Math.max(0, xTop - baseX);
                          return (
                            <React.Fragment>
                              {sumCat === 0 && zeroMode === "ghost" ? (
                                <rect
                                  x={baseX}
                                  y={y}
                                  width={ghostSizePx}
                                  height={stackHeight}
                                  rx={cornerRadius}
                                  ry={cornerRadius}
                                  style={{
                                    fill: ghostFill,
                                    opacity: ghostOpacity,
                                  }}
                                />
                              ) : (
                                <>
                              <defs>
                                <clipPath id={clipId}>
                                  <rect
                                    x={baseX}
                                    y={y}
                                    width={totalW}
                                    height={stackHeight}
                                    rx={cornerRadius}
                                    ry={cornerRadius}
                                  />
                                </clipPath>
                              </defs>
                              {visibleSeries.map((s, si) => {
                                const color =
                                  (colorMap &&
                                    colorMap[(s && s.__idx) ?? si]) ||
                                  resolvedColors[
                                    ((s && s.__idx) ?? si) %
                                      resolvedColors.length
                                  ] ||
                                  "#3b82f6";
                                const vRaw = s.data[ci];
                                if (vRaw == null || !isFinite(vRaw))
                                  return null;
                                const v = Number(vRaw);
                                const xRight = xScale(
                                  cum + v,
                                  p.min,
                                  p.max,
                                  containerW,
                                  p.left,
                                  p.right,
                                  true,
                                );
                                const xLeft = xScale(
                                  cum,
                                  p.min,
                                  p.max,
                                  containerW,
                                  p.left,
                                  p.right,
                                  true,
                                );
                                const w = Math.max(0, xRight - xLeft);
                                cum += v;
                                return (
                                  <React.Fragment key={`stack-${ci}-${si}`}>
                                    <rect
                                      x={xLeft}
                                      y={y}
                                      width={w}
                                      height={stackHeight}
                                      rx={0}
                                      ry={0}
                                      style={{ fill: color }}
                                      clipPath={`url(#${clipId})`}
                                    />
                                    {dataLabelsEnabled && w > 12 && (
                                      <text
                                        x={xLeft + w + 6}
                                        y={y + stackHeight / 2 + 4}
                                        style={{ fill: color }}
                                        fontSize={dataLabelFontSize}
                                        textAnchor="start"
                                      >
                                        {formatDataValue(v)}
                                      </text>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })()}
                    </g>
                  );
                })}
              </g>
              {hover && (
                <g clipPath={`url(#plotClip-${localId})`}>
                  <line
                    x1={p.left}
                    x2={containerW - p.right}
                    y1={hover.y}
                    y2={hover.y}
                    stroke={gridColor}
                    strokeDasharray={dash}
                  />
                </g>
              )}
              {title && (
                <text
                  x={p.left}
                  y={p.top - 6}
                  fill={axisLabelColor}
                  fontSize={axisFontSize}
                >
                  {title}
                </text>
              )}
            </svg>
          </div>
          <div className="xAxis" style={{ overflow: "hidden" }}>
            <div ref={xAxisContentRef} style={{ width: containerW }}>
              <svg width={containerW} height={axisXHeight}>
                <g>
                  {ticksX.map((tv, i) => {
                    const x = xScale(
                      tv,
                      p.min,
                      p.max,
                      containerW,
                      p.left,
                      p.right,
                      false,
                    );
                    return (
                      <text
                        key={`xl-${i}`}
                        x={x}
                        y={axisXHeight - 6}
                        fill={axisLabelColor}
                        fontSize={axisFontSize}
                        textAnchor="middle"
                      >
                        {formatXLabel(tv, tickStep)}
                      </text>
                    );
                  })}
                </g>
              </svg>
            </div>
          </div>{" "}
        </div>
      </div>
    </div>
  );
};

export default BarChartNew;
